import {
  Classification,
  TextFading,
  YesObject
} from '@yes.technology/react-toolkit'
import ContactObjectCard from 'component/ContactObjectCard'
import GenericObjectCard from 'component/GenericObjectCard'
import TaskObjectCard from 'component/TaskObjectCard'
import TransportOrderCard from 'component/TransportOrderCard'
import React from 'react'
import splitIntoChunks from 'shared/utils/array/splitIntoChunks'
import {
  AssociatedUsersRelationsProps,
  CardModel,
  ColumnProps
} from '../../Kanban.types'
import useMoveTaskCardAnimation from '../../hooks/useMoveTaskCardAnimation'
import {
  CardContainer,
  ColumnCard,
  ColumnsContainer,
  KanbanColumnTitle
} from './KanbanAutoColumns.styles'
import { KanbanContext } from './useKanbanContext'

const validCardModels = {
  GenericObjectCard,
  TaskObjectCard,
  ContactObjectCard,
  TransportOrderCard
} as Record<CardModel, React.FC<{ returnOnFinish?: boolean }>>

type KanbanAutoColumnsProps = {
  relationObjects: YesObject[]
  associatedUsersRelations: AssociatedUsersRelationsProps
  groupedYesObjects: Record<string, YesObject[]>
  cardModel: CardModel
  columnLabelsTextFading?: boolean
  currentSection: number
  columnsPerPage: number
  sections: Record<string, YesObject[]>
  columnsOverrideAttributes: Record<string, ColumnProps>
  statusActionClassification?: Classification[]
  returnOnFinishedInteraction?: boolean
  onMoveTask: (
    taskUuid: string,
    newStatusColumn: string,
    setIsFadeInAnimationStarted: React.Dispatch<React.SetStateAction<boolean>>
  ) => void
}

function KanbanAutoColumns({
  relationObjects,
  associatedUsersRelations,
  groupedYesObjects,
  cardModel,
  currentSection,
  columnsPerPage,
  sections,
  columnsOverrideAttributes,
  statusActionClassification,
  returnOnFinishedInteraction = false,
  columnLabelsTextFading = false,
  onMoveTask
}: KanbanAutoColumnsProps) {
  const CardComponent = validCardModels[cardModel]

  const splittedColumns = splitIntoChunks(Object.keys(sections), columnsPerPage)

  const currentSectionColumns = splittedColumns[currentSection] ?? []

  const {
    targetCardUuid,
    isFadeOutAnimationStarted,
    isFadeInAnimationStarted,
    onStartTaskMove
  } = useMoveTaskCardAnimation({
    onMoveTask
  })

  return (
    <KanbanContext.Provider
      value={{
        onStatusChange: onStartTaskMove,
        statusActionClassification,
        associatedUsersRelations
      }}
    >
      <ColumnsContainer $columnsPerPage={columnsPerPage}>
        {currentSectionColumns.map((column, columnIndex) => {
          const currentColumnObjects = groupedYesObjects[column] || []

          const columnRelationObject = relationObjects.find(
            (relationObject) => relationObject.code === column
          )

          const columnLabel =
            columnsOverrideAttributes?.[column]?.label ||
            columnRelationObject?.des

          const currentColumnTextFading =
            typeof columnsOverrideAttributes?.[column]?.text_fading ===
            'boolean'
              ? columnsOverrideAttributes[column].text_fading
              : columnLabelsTextFading

          return (
            <div key={`${column}_${columnIndex}`}>
              <ColumnCard
                fullHeight
                isColoredBackground={cardModel === 'TransportOrderCard'}
              >
                <KanbanColumnTitle>
                  {currentColumnTextFading ? (
                    <TextFading>{columnLabel}</TextFading>
                  ) : (
                    columnLabel
                  )}
                </KanbanColumnTitle>
                {currentColumnObjects.map((object, objectIndex) => (
                  <CardContainer
                    key={`${object.uuid}_${objectIndex}`}
                    $isFadingOut={
                      isFadeOutAnimationStarted &&
                      object.uuid === targetCardUuid
                    }
                    $isFadingIn={
                      isFadeInAnimationStarted && object.uuid === targetCardUuid
                    }
                  >
                    <CardComponent
                      returnOnFinish={returnOnFinishedInteraction}
                      {...object}
                    />
                  </CardContainer>
                ))}
              </ColumnCard>
            </div>
          )
        })}
      </ColumnsContainer>
    </KanbanContext.Provider>
  )
}

export default KanbanAutoColumns
